/**
 * "My Timesheet" for a pipeline project (domain-model-transition P7a).
 *
 * WHAT THIS REPLACES, AND WHY IT IS NOT A COSMETIC SWAP. This tab used to render the shared
 * `GeneralTimesheetView`, which writes `/log_time` → `billed_time` — the LEGAL billing spine,
 * billable through the shared `invoices` table. A pipeline day recorded there could also exist on
 * the construction ticket spine, and bill twice (stage-8 review H3). P7a re-plumbs self-entry onto
 * the construction spine and the server now REFUSES the old door for `vertical='pipeline'`
 * matters — one vertical only, because `ncalpro`/`ncaldes` are `engineering` and bill through
 * `/log_time` today.
 *
 * The client routes on the MATTER, never on the caller's tenant: a deployment carries pipeline,
 * engineering and general matters side by side. And the guard is the boundary regardless of what
 * this file does — the server refuses, the client merely avoids asking.
 *
 * NOTHING HERE IS CLASSIFIED. A person states what they worked and whether it was travel; what
 * the statute makes of that is P7b's, behind a counsel signature this phase does not wait for.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import PipelineProjectService, {
  PersonDayService,
  type MyTimesheetEntry,
} from '@frontend/AppSettings/services/PipelineProjectService';

interface MyFieldTimesheetViewProps {
  matterid: string;
  matterName?: string;
}

interface DraftEntry {
  hours: string;
  hourType: 'working' | 'travel';
  costCodeId: string;
}

const EMPTY_ENTRY: DraftEntry = { hours: '', hourType: 'working', costCodeId: '' };

// LOCAL date, not UTC (review R8): toISOString is UTC, so every evening west of Greenwich it
// seeded TOMORROW as the day being recorded — a wrong default a tired field worker corrects
// only if they notice it.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function MyFieldTimesheetView({ matterid, matterName }: MyFieldTimesheetViewProps) {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(todayISO);
  const [phaseId, setPhaseId] = useState('');
  const [entries, setEntries] = useState<DraftEntry[]>([{ ...EMPTY_ENTRY }]);
  const [error, setError] = useState<string | null>(null);

  const myDays = useQuery({
    queryKey: ['MyPersonDays', matterid],
    queryFn: () => PersonDayService.listMyDays(),
  });
  const phases = useQuery({
    queryKey: ['ProjectPhases', matterid],
    queryFn: () => PipelineProjectService.getPhases(matterid),
  });
  const costCodes = useQuery({
    queryKey: ['ProjectCostCodes', matterid],
    queryFn: () => PipelineProjectService.getCostCodes(matterid),
  });

  const dayTotal = useMemo(
    () => entries.reduce((sum, e) => sum + (Number(e.hours) || 0), 0),
    [entries],
  );

  const record = useMutation({
    mutationFn: async () => {
      // Refused, never silently dropped — the house doctrine for a partial row.
      const incomplete = entries.some((e) => e.hours === '' || !(Number(e.hours) > 0));
      if (incomplete) {
        return Promise.reject(new Error('Every line needs hours greater than zero.'));
      }
      const body = {
        matterid,
        ticket_date: date,
        ...(phaseId ? { project_phase_id: phaseId } : {}),
        entries: entries.map((e): MyTimesheetEntry => ({
          hours: Number(e.hours),
          hour_type_entered: e.hourType,
          ...(e.costCodeId ? { project_cost_code_id: e.costCodeId } : {}),
        })),
      };
      return PersonDayService.recordMyDay(body);
    },
    onSuccess: () => {
      setEntries([{ ...EMPTY_ENTRY }]);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['MyPersonDays', matterid] });
      void queryClient.invalidateQueries({ queryKey: ['FieldTickets', matterid] });
    },
    onError: (err: unknown) => setError((err as Error)?.message || 'Could not record your day.'),
  });

  const setEntry = (i: number, patch: Partial<DraftEntry>) =>
    setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));

  const canSubmit = date !== '' && entries.length > 0 && !record.isPending;

  return (
    <div className="pipeline-panel">
      <h3 className="pipeline-panel-title">My Timesheet</h3>
      <p className="pipeline-panel-subtitle">
        Your own hours on {matterName || 'this project'}. Recorded against the project&apos;s field
        records — not the billing timesheet.
      </p>

      <form
        className="pipeline-form"
        onSubmit={(e) => { e.preventDefault(); if (canSubmit) record.mutate(); }}
      >
        <div className="pipeline-form-row">
          <label>
            Date
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label>
            Phase
            <select value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
              <option value="">(none)</option>
              {(phases.data ?? []).map((p: { id: string; name: string }) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </label>
        </div>

        {entries.map((entry, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="pipeline-form-row" key={i}>
            <label>
              Hours
              <input
                type="number" min="0" step="0.25" value={entry.hours}
                onChange={(e) => setEntry(i, { hours: e.target.value })}
              />
            </label>
            <label>
              Type
              <select
                value={entry.hourType}
                onChange={(e) => setEntry(i, {
                  hourType: e.target.value as DraftEntry['hourType'],
                })}
              >
                <option value="working">Working</option>
                <option value="travel">Travel</option>
              </select>
            </label>
            <label>
              Cost code
              <select
                value={entry.costCodeId}
                onChange={(e) => setEntry(i, { costCodeId: e.target.value })}
              >
                <option value="">(project default)</option>
                {(costCodes.data ?? []).map((c: { id: string; code: string; name: string }) => (
                  <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                ))}
              </select>
            </label>
            {entries.length > 1 && (
              <button
                type="button" className="pipeline-secondary-btn"
                onClick={() => setEntries((prev) => prev.filter((_, idx) => idx !== i))}
              >
                Remove
              </button>
            )}
          </div>
        ))}

        <p className="pipeline-muted">Day total: {dayTotal || 0} h</p>

        <div className="pipeline-form-actions">
          <button
            type="button" className="pipeline-secondary-btn"
            onClick={() => setEntries((prev) => [...prev, { ...EMPTY_ENTRY }])}
          >
            Add line
          </button>
          <button type="submit" className="pipeline-action-btn" disabled={!canSubmit}>
            {record.isPending ? 'Recording…' : 'Record my day'}
          </button>
        </div>
        {error && <p className="pipeline-form-error" role="alert">{error}</p>}
      </form>

      <h4 className="pipeline-panel-title">My recent days</h4>
      {myDays.isLoading && <p className="pipeline-muted">Loading your days…</p>}
      {myDays.isError && <p className="pipeline-muted">Your days could not be loaded.</p>}
      {myDays.isSuccess && (myDays.data ?? []).length === 0 && (
        <p className="pipeline-muted">No days recorded yet.</p>
      )}
      {(myDays.data ?? []).length > 0 && (
        <table className="pipeline-table">
          <thead>
            <tr><th>Date</th><th>Status</th><th>Shift start</th></tr>
          </thead>
          <tbody>
            {(myDays.data ?? []).map((day) => (
              <tr key={day.id}>
                <td>{day.work_date}</td>
                <td>{day.status === 'absent' ? 'Absent' : 'Worked'}</td>
                {/* No invented default: blank means "not recorded", which is what it means. */}
                <td>{day.shift_start_time ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
